import { atom, getDefaultStore } from 'jotai';
import { getActiveOrgId } from '@/lib/active-org';
import { Config } from '@/utils/config';

export type InsufficientCreditsDetails = {
  actionType?: string;
  required: number;
  remaining: number;
  billingUrl?: string;
  message?: string;
};

export const insufficientCreditsAtom = atom<InsufficientCreditsDetails | null>(null);

function normalizeDetails(value: Partial<InsufficientCreditsDetails> | undefined) {
  if (!value || typeof value.required !== 'number' || typeof value.remaining !== 'number') {
    return null;
  }

  return {
    actionType: typeof value.actionType === 'string' ? value.actionType : undefined,
    required: value.required,
    remaining: value.remaining,
    billingUrl: typeof value.billingUrl === 'string' ? value.billingUrl : undefined,
    message: typeof value.message === 'string' ? value.message : undefined,
  } satisfies InsufficientCreditsDetails;
}

export async function parseInsufficientCreditsResponse(
  response: Response,
): Promise<InsufficientCreditsDetails | null> {
  if (response.status !== 402) return null;

  const payload = (await response
    .clone()
    .json()
    .catch(() => null)) as { code?: string; details?: Partial<InsufficientCreditsDetails> } | null;

  if (payload?.code !== 'INSUFFICIENT_AI_CREDITS') return null;
  return normalizeDetails(payload.details);
}

export function parseInsufficientCreditsMessage(
  message: string,
): InsufficientCreditsDetails | null {
  try {
    const payload = JSON.parse(message) as {
      code?: string;
      details?: Partial<InsufficientCreditsDetails>;
    };
    if (payload?.code !== 'INSUFFICIENT_AI_CREDITS') return null;
    return normalizeDetails(payload.details);
  } catch {
    return null;
  }
}

export function showInsufficientCreditsModal(details: InsufficientCreditsDetails) {
  getDefaultStore().set(insufficientCreditsAtom, details);
}

// Callers that catch an error after an insufficient-credits response (already surfaced via the
// modal above) check this to skip showing a redundant toast for the same failure.
export function isInsufficientCreditsModalOpen(): boolean {
  return getDefaultStore().get(insufficientCreditsAtom) !== null;
}

export function dismissInsufficientCreditsModal() {
  getDefaultStore().set(insufficientCreditsAtom, null);
}

export function openBillingForCredits(details: InsufficientCreditsDetails | null) {
  // billingUrl comes from the backend (org-scoped). The fallback rebuilds it from the
  // builder's active org — never a bare /settings/billing, which is an org-scoped route.
  const orgId = getActiveOrgId();
  const billingPath = details?.billingUrl || (orgId != null ? `/${orgId}/settings/billing` : '/');
  const billingUrl = new URL(billingPath, Config.NEXT_URL);
  billingUrl.searchParams.set('returnUrl', window.location.href);
  window.location.href = billingUrl.toString();
}
